import {
  useGetOrganizationDetailsQuery,
  useGetOrganizationCoverageAreaSettingsQuery,
} from '@/services/organizations';
import { Box, Button, Card, Spinner } from '@palmetto/palmetto-components';
import { useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ProgramType, StateLicenseStatus } from 'types';

import { AutoAcceptLeadsCard } from './AutoAcceptLeadsCard';
import { CapacityStateCard } from './CapacityStateCard';
import { getStateName } from './stateNames';

export function Capacity() {
  const { alias } = useParams<{ alias: string }>();
  const { data: org } = useGetOrganizationDetailsQuery({ alias: alias! });
  const { data: coverageSettingsData, isLoading } = useGetOrganizationCoverageAreaSettingsQuery({ alias: alias! });
  const coverageSettings = coverageSettingsData ?? [];

  const [selectedState, setSelectedState] = useState<string>('all');

  const activeLicenses = useMemo(
    () =>
      org?.stateLicenseNumbers?.filter(
        (l) => l.status === StateLicenseStatus.active && l.programType === ProgramType.consumerSolar,
      ) ?? [],
    [org],
  );

  const availableStates = useMemo(
    () => [...new Set(activeLicenses.map((l) => l.state))].toSorted(),
    [activeLicenses],
  );

  const visibleStates = useMemo(
    () => (selectedState === 'all' ? availableStates : availableStates.filter((s) => s === selectedState)),
    [availableStates, selectedState],
  );

  const handleSelectAll = useCallback(() => setSelectedState('all'), []);

  const handleSelectState = useCallback((state: string) => () => setSelectedState(state), []);

  return (
    <div className="flex flex-col gap-10">
      <AutoAcceptLeadsCard />

      {availableStates.length > 0 && (
        <Box direction="row" childGap="sm">
          <Button
            type="button"
            variant={selectedState === 'all' ? 'primary' : 'secondary'}
            onClick={handleSelectAll}
            size="sm"
          >
            All
          </Button>
          {availableStates.map((state) => (
            <Button
              key={state}
              type="button"
              variant={selectedState === state ? 'primary' : 'secondary'}
              onClick={handleSelectState(state)}
              size="sm"
            >
              {getStateName(state)} ({state})
            </Button>
          ))}
        </Box>
      )}

      {isLoading ? (
        <Box alignItems="center" padding="xl">
          <Spinner size="lg" />
        </Box>
      ) : activeLicenses.length === 0 ? (
        <Card padding="lg">
          <span>No licensed states found for this organization.</span>
        </Card>
      ) : (
        visibleStates.map((state) => {
          const setting = coverageSettings.find(
            (s) => s.state === state && s.programType === ProgramType.consumerSolar,
          );
          return (
            <CapacityStateCard
              key={state}
              alias={alias!}
              state={state}
              programType={ProgramType.consumerSolar}
              setting={setting}
            />
          );
        })
      )}
    </div>
  );
}
